<template>
  <InputWrapper
    v-slot="{ inputId }"
    :input-id="id"
    :label="t('glCodes.glCodes', 2)"
    class="shipment-gl-code-filter w-2"
    optional
  >
    <GLCodeMultiSelect
      :id="inputId"
      :model-value="glCodes"
      :loading="availableGLCodes.loading"
      :options="availableGLCodes.data"
      @update:model-value="onUpdate"
    />
  </InputWrapper>
</template>

<script lang="ts" setup>
import { computed, onMounted, toRefs } from 'vue';
import { useI18n } from 'vue-i18n';

import { useModelValue } from '@rygen/rygen-ui/composables';
import { GLCode } from '@rygen/rygen-ui/model';
import { notNull } from '@rygen/rygen-ui/util';

import { ShipmentSearchCriteria, SlimGLCode } from '@/model';
import { useGLCodeStore } from '@/stores/GLCode';

import GLCodeMultiSelect from '@/components/glCode/GLCodeMultiSelect.vue';
import InputWrapper from '@rygen/rygen-ui/components/form/InputWrapper.vue';

const props = defineProps({
  id: {
    type: String,
    required: true,
  },
  modelValue: {
    type: ShipmentSearchCriteria,
    default: () => new ShipmentSearchCriteria(),
  },
});
const { id, modelValue } = toRefs(props);
const emit = defineEmits<{
  (event: 'update:model-value', newVal: ShipmentSearchCriteria): void;
}>();

const { availableGLCodes, loadGLCodes } = useGLCodeStore();
const { t } = useI18n();
const { internalValue } = useModelValue(modelValue, {
  emit,
  factoryMethod: (opts: Partial<ShipmentSearchCriteria>) =>
    new ShipmentSearchCriteria(opts),
});

/**
 * The `glCodes` from the search params converted to full `GLCode`s
 *
 * @see `SlimGLCode`
 **/
const glCodes = computed(() => {
  return (
    internalValue.value.glCodes
      ?.map((g) => {
        return availableGLCodes.value.data?.find(
          (glCode) => glCode.code === g.code,
        );
      })
      ?.filter(notNull) ?? []
  );
});

/** Convert the selected `GLCode`s to `SlimGLCode`s for the HTTP request */
const onUpdate = (newGLCodes: GLCode[]) => {
  internalValue.value.glCodes = newGLCodes.map((g) => new SlimGLCode(g));
};

onMounted(() => {
  loadGLCodes();
});
</script>
